import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  LogOut, Plus, Edit, Trash2, FileText, Video, Music, Image, 
  X, Download, FolderPlus, Tag, Eye, Paperclip, ArrowLeft,
  File, Bold, Italic, List, Link2, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  GripVertical
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import LoadingQuip from "@/components/LoadingQuip";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTheme } from "@/contexts/ThemeContext";
import DarkModeToggle from "@/components/DarkModeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

// Sortable Resource Card Component
function SortableResourceCard({ resource, onPreview, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: resource.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      className={`p-4 bg-white ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''}`}
    >
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded mt-1"
            title="Drag to reorder"
          >
            <GripVertical className="w-5 h-5 text-gray-400" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1" style={{ color: '#092036' }}>
              {resource.title}
            </h3>
            {resource.description && (
              <p className="text-sm mb-2 line-clamp-2" style={{ color: '#70767C' }}>
                {resource.description}
              </p>
            )}
            <div className="flex flex-wrap gap-2 items-center">
              {resource.category_name && (
                <Badge variant="outline" style={{ borderColor: '#102862', color: '#102862' }}>
                  {resource.category_name}
                </Badge>
              )}
              {resource.tags?.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                </Badge>
              ))}
              {resource.attachments?.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  <Paperclip className="w-3 h-3 mr-1" />
                  {resource.attachments.length} file{resource.attachments.length > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPreview(resource)}
            className="gap-1"
          >
            <Eye className="w-4 h-4" />
            Preview
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(resource)}
            className="gap-1"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(resource)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

// Rich Text Editor Component
function RichTextEditor({ value, onChange, placeholder, rows = 6 }) {
  const editorRef = useRef(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [savedSelection, setSavedSelection] = useState(null);

  // Save selection before opening link dialog
  const saveSelection = () => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        setSavedSelection(selection.getRangeAt(0).cloneRange());
        setLinkText(selection.toString());
      }
    }
  };

  // Restore selection after link dialog
  const restoreSelection = () => {
    if (savedSelection && editorRef.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);
    }
  };

  const execCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleContentChange();
  };

  const handleBold = () => execCommand('bold');
  const handleItalic = () => execCommand('italic');
  
  const handleBulletList = () => execCommand('insertUnorderedList');
  
  const handleAlignLeft = () => execCommand('justifyLeft');
  const handleAlignCenter = () => execCommand('justifyCenter');
  const handleAlignRight = () => execCommand('justifyRight');
  const handleAlignJustify = () => execCommand('justifyFull');
  
  const handleLink = () => {
    saveSelection();
    setLinkUrl("");
    setShowLinkDialog(true);
  };

  const insertLink = () => {
    if (linkUrl) {
      restoreSelection();
      editorRef.current?.focus();
      
      if (linkText) {
        // Insert link with text
        const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${linkText}</a>`;
        document.execCommand('insertHTML', false, linkHtml);
      } else {
        // Wrap selection in link
        document.execCommand('createLink', false, linkUrl);
      }
      
      handleContentChange();
    }
    setShowLinkDialog(false);
    setLinkUrl("");
    setLinkText("");
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleContentChange();
  };

  return (
    <div className="border rounded-md overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-gray-50 border-b">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBold}
          className="h-8 w-8 p-0"
          title="Bold (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleItalic}
          className="h-8 w-8 p-0"
          title="Italic (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBulletList}
          className="h-8 w-8 p-0"
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleLink}
          className="h-8 w-8 p-0"
          title="Insert Link"
        >
          <Link2 className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleAlignLeft}
          className="h-8 w-8 p-0"
          title="Align Left"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleAlignCenter}
          className="h-8 w-8 p-0"
          title="Align Center"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleAlignRight}
          className="h-8 w-8 p-0"
          title="Align Right"
        >
          <AlignRight className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleAlignJustify}
          className="h-8 w-8 p-0"
          title="Justify"
        >
          <AlignJustify className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="p-3 min-h-[150px] focus:outline-none prose prose-sm max-w-none"
        style={{ minHeight: `${rows * 24}px` }}
        onInput={handleContentChange}
        onPaste={handlePaste}
        dangerouslySetInnerHTML={{ __html: value || '' }}
        data-placeholder={placeholder}
      />
      
      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Link Text</Label>
              <Input
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Display text (optional)"
              />
            </div>
            <div>
              <Label>URL *</Label>
              <Input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
                Cancel
              </Button>
              <Button onClick={insertLink} disabled={!linkUrl}>
                Insert Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        [contenteditable] a {
          color: #2563eb;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getFileIcon(fileType) {
  switch (fileType) {
    case 'video': return <Video className="w-4 h-4" />;
    case 'audio': return <Music className="w-4 h-4" />;
    case 'image': return <Image className="w-4 h-4" />;
    case 'pdf': return <FileText className="w-4 h-4" />;
    default: return <File className="w-4 h-4" />;
  }
}

export default function AdminPracticeResources({ user, logout }) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Dialog states
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  
  // Form states
  const [editingResource, setEditingResource] = useState(null);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  const [previewResource, setPreviewResource] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryDesc, setNewCategoryDesc] = useState("");
  
  const [resourceForm, setResourceForm] = useState({
    title: "",
    description: "",
    content: "",
    category_id: "",
    tags: "",
    files: [],
    existingAttachments: []
  });
  
  const [saving, setSaving] = useState(false);
  
  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = resources.findIndex(r => r.id === active.id);
      const newIndex = resources.findIndex(r => r.id === over.id);
      
      const newOrder = arrayMove(resources, oldIndex, newIndex);
      setResources(newOrder);
      
      // Save the new order to backend
      try {
        const resourceIds = newOrder.map(r => r.id);
        console.log("Saving new order:", resourceIds);
        
        const response = await axios.put(
          `${API}/admin/practice-resources/reorder`,
          { resource_ids: resourceIds },
          { headers }
        );
        console.log("Reorder response:", response.data);
        toast.success("Order saved");
      } catch (error) {
        console.error("Error saving order:", error);
        console.error("Error response:", error.response?.data);
        toast.error("Failed to save order");
        // Revert on error
        fetchResources();
      }
    }
  };

  const fetchResources = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/practice-resources`, { headers });
      setResources(response.data);
    } catch (error) {
      toast.error("Error loading resources");
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/resource-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error loading categories");
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchResources(), fetchCategories()]);
      setLoading(false);
    };
    loadData();
  }, [fetchResources, fetchCategories]);

  const openCreateDialog = () => {
    setEditingResource(null);
    setResourceForm({
      title: "",
      description: "",
      content: "",
      category_id: "",
      tags: "",
      files: [],
      existingAttachments: []
    });
    setShowResourceDialog(true);
  };

  const openEditDialog = (resource) => {
    setEditingResource(resource);
    setResourceForm({
      title: resource.title,
      description: resource.description || "",
      content: resource.content || "",
      category_id: resource.category_id || "",
      tags: resource.tags?.join(", ") || "",
      files: [],
      existingAttachments: resource.attachments || []
    });
    setShowResourceDialog(true);
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setResourceForm(prev => ({
      ...prev,
      files: [...prev.files, ...newFiles]
    }));
  };

  const removeNewFile = (index) => {
    setResourceForm(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const removeExistingAttachment = (filePath) => {
    setResourceForm(prev => ({
      ...prev,
      existingAttachments: prev.existingAttachments.filter(a => a.file_path !== filePath)
    }));
  };

  const saveResource = async () => {
    if (!resourceForm.title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", resourceForm.title);
      formData.append("description", resourceForm.description);
      formData.append("content", resourceForm.content);
      formData.append("category_id", resourceForm.category_id || "");
      formData.append("tags", resourceForm.tags);
      
      // Add files
      resourceForm.files.forEach((file) => {
        formData.append("files", file);
      });
      
      if (editingResource) {
        // Keep existing attachments
        formData.append("current_attachments", JSON.stringify(
          resourceForm.existingAttachments.map(a => a.file_path)
        ));
        
        await axios.put(
          `${API}/admin/practice-resources/${editingResource.id}`,
          formData,
          { headers }  // Don't set Content-Type for FormData - let browser set it with boundary
        );
        toast.success("Resource updated");
      } else {
        await axios.post(
          `${API}/admin/practice-resources`,
          formData,
          { headers }  // Don't set Content-Type for FormData - let browser set it with boundary
        );
        toast.success("Resource created");
      }
      
      setShowResourceDialog(false);
      fetchResources();
    } catch (error) {
      console.error("Save error:", error);
      // Handle Pydantic validation errors (422) which return an array of error objects
      let errorMessage = "Error saving resource";
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e).join(", ");
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const deleteResource = async () => {
    if (!resourceToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/practice-resources/${resourceToDelete.id}`, { headers });
      toast.success("Resource deleted");
      setShowDeleteDialog(false);
      setResourceToDelete(null);
      fetchResources();
    } catch (error) {
      toast.error("Error deleting resource");
    }
  };

  const createCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Please enter a category name");
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append("name", newCategoryName.trim());
      if (newCategoryDesc.trim()) {
        formData.append("description", newCategoryDesc.trim());
      }
      
      await axios.post(`${API}/admin/resource-categories`, formData, { headers });
      toast.success("Category created");
      setNewCategoryName("");
      setNewCategoryDesc("");
      setShowCategoryDialog(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error creating category");
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!confirm("Delete this category? Resources will become uncategorized.")) return;
    
    try {
      await axios.delete(`${API}/admin/resource-categories/${categoryId}`, { headers });
      toast.success("Category deleted");
      fetchCategories();
      fetchResources();
    } catch (error) {
      toast.error("Error deleting category");
    }
  };

  const getAuthenticatedFileUrl = async (filePath) => {
    const parts = filePath.split('/');
    const subfolder = parts[parts.length - 2];
    const filename = parts[parts.length - 1];
    
    try {
      const response = await axios.get(
        `${API}/practice-resources/files/${subfolder}/${filename}`,
        { headers, responseType: 'blob' }
      );
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error("Error loading file:", error);
      return null;
    }
  };

  const downloadFile = async (filePath, filename) => {
    try {
      const url = await getAuthenticatedFileUrl(filePath);
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error("Error downloading file");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: isDarkMode ? '#092036' : '#F4F4F4' }}>
      {/* Header */}
      <header className="shadow-sm sticky top-0 z-50" style={{ backgroundColor: isDarkMode ? '#092036' : 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate("/")}>
              <img src={LOGO_URL} alt="Logo" className="h-10" style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
              <span className="hidden sm:block text-lg font-semibold" style={{ color: isDarkMode ? '#ffffff' : '#102862' }}>
                Practice Resources
              </span>
            </div>
            <div className="flex items-center gap-3">
              <DarkModeToggle />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin")}
                className="gap-2"
                style={{ color: isDarkMode ? '#e2e8f0' : undefined }}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
              >
                <LogOut className="w-5 h-5" style={{ color: '#9E3639' }} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
            Practice Resources
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              onClick={() => setShowCategoryDialog(true)}
              variant="outline"
              size="sm"
              className="gap-2 w-full sm:w-auto"
            >
              <FolderPlus className="w-4 h-4" />
              Manage Categories
            </Button>
            <Button
              onClick={openCreateDialog}
              size="sm"
              className="gap-2 text-white w-full sm:w-auto"
              style={{ background: '#102862' }}
            >
              <Plus className="w-4 h-4" />
              Add Resource
            </Button>
          </div>
        </div>

        {loading ? (
          <Card className="p-12 bg-white">
            <LoadingQuip size="lg" />
          </Card>
        ) : resources.length === 0 ? (
          <Card className="p-12 text-center bg-white">
            <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: '#DADEEO' }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: '#70767C' }}>
              No resources yet
            </h3>
            <p style={{ color: '#70767C' }}>
              Add your first practice resource for students
            </p>
            <Button
              onClick={openCreateDialog}
              className="mt-4 gap-2 text-white"
              style={{ background: '#102862' }}
            >
              <Plus className="w-4 h-4" />
              Add Resource
            </Button>
          </Card>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={resources.map(r => r.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <GripVertical className="w-4 h-4" />
                  <span>Drag resources to reorder them</span>
                </div>
                {resources.map(resource => (
                  <SortableResourceCard
                    key={resource.id}
                    resource={resource}
                    onPreview={(r) => {
                      setPreviewResource(r);
                      setShowPreviewDialog(true);
                    }}
                    onEdit={openEditDialog}
                    onDelete={(r) => {
                      setResourceToDelete(r);
                      setShowDeleteDialog(true);
                    }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </main>

      {/* Resource Dialog */}
      <Dialog open={showResourceDialog} onOpenChange={setShowResourceDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto light-form-card">
          <DialogHeader>
            <DialogTitle>
              {editingResource ? "Edit Resource" : "Add New Resource"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={resourceForm.title}
                onChange={(e) => setResourceForm({...resourceForm, title: e.target.value})}
                placeholder="Resource title"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={resourceForm.description}
                onChange={(e) => setResourceForm({...resourceForm, description: e.target.value})}
                placeholder="Brief description of the resource"
                rows={2}
              />
            </div>
            
            <div>
              <Label>Content</Label>
              <RichTextEditor
                value={resourceForm.content}
                onChange={(content) => setResourceForm({...resourceForm, content})}
                placeholder="Full text content (optional) - Use toolbar for formatting"
                rows={8}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={resourceForm.category_id || "none"}
                  onValueChange={(value) => setResourceForm({...resourceForm, category_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No category</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={resourceForm.tags}
                  onChange={(e) => setResourceForm({...resourceForm, tags: e.target.value})}
                  placeholder="e.g., surgery, technique, beginner"
                />
              </div>
            </div>
            
            <div>
              <Label>Attachments (PDFs, Images, Videos, Audio)</Label>
              <div className="mt-2">
                <Input
                  type="file"
                  multiple
                  accept="image/*,video/*,audio/*,.pdf"
                  onChange={handleFileChange}
                  className="cursor-pointer"
                />
              </div>
              
              {/* Existing attachments */}
              {resourceForm.existingAttachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-medium text-gray-600">Existing files:</p>
                  {resourceForm.existingAttachments.map((att, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        {getFileIcon(att.file_type)}
                        <span className="text-sm">{att.filename}</span>
                        <span className="text-xs text-gray-500">({formatFileSize(att.file_size)})</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeExistingAttachment(att.file_path)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* New files to upload */}
              {resourceForm.files.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-medium text-gray-600">New files to upload:</p>
                  {resourceForm.files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                      <div className="flex items-center gap-2">
                        <File className="w-4 h-4" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeNewFile(idx)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowResourceDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={saveResource}
                disabled={saving}
                className="text-white"
                style={{ background: '#102862' }}
              >
                {saving ? "Saving..." : (editingResource ? "Update" : "Create")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-md light-form-card">
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Existing categories */}
            {categories.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600">Existing categories:</p>
                {categories.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span>{cat.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteCategory(cat.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add new category */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-600 mb-2">Add new category:</p>
              <div className="space-y-2">
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Category name"
                />
                <Input
                  value={newCategoryDesc}
                  onChange={(e) => setNewCategoryDesc(e.target.value)}
                  placeholder="Description (optional)"
                />
                <Button
                  onClick={createCategory}
                  className="w-full gap-2 text-white"
                  style={{ background: '#102862' }}
                >
                  <Plus className="w-4 h-4" />
                  Add Category
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewResource?.title}</DialogTitle>
          </DialogHeader>
          {previewResource && (
            <div className="space-y-4 mt-4">
              {previewResource.description && (
                <p style={{ color: '#70767C' }}>{previewResource.description}</p>
              )}
              
              <div className="flex flex-wrap gap-2">
                {previewResource.category_name && (
                  <Badge variant="outline" style={{ borderColor: '#102862', color: '#102862' }}>
                    {previewResource.category_name}
                  </Badge>
                )}
                {previewResource.tags?.map(tag => (
                  <Badge key={tag} variant="secondary">
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
              
              {previewResource.content && (
                <div className="prose max-w-none">
                  <h4 className="text-sm font-semibold mb-2" style={{ color: '#092036' }}>Content:</h4>
                  <div 
                    className="prose prose-sm max-w-none text-sm p-4 bg-gray-50 rounded-lg"
                    dangerouslySetInnerHTML={{ __html: previewResource.content }}
                  />
                </div>
              )}
              
              {previewResource.attachments?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2" style={{ color: '#092036' }}>Attachments:</h4>
                  <div className="space-y-2">
                    {previewResource.attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {getFileIcon(att.file_type)}
                          <div>
                            <p className="text-sm font-medium">{att.filename}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(att.file_size)}</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadFile(att.file_path, att.filename)}
                          className="gap-1"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Resource</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{resourceToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteResource}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
